// src/pages/PagoPensionResultadoPage.js
//
// A donde Wompi redirige después de un pago (sobre todo PSE, que sale
// del sitio). El webhook-router puede tardar unos segundos en llegar
// desde Ecolab -- esta pantalla reintenta consultar el estado de la
// cuota unas cuantas veces antes de darse por vencida.

import React, { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, CircularProgress, Alert, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { meService, pensionService } from '../services/api';

const REINTENTOS = 6;
const ESPERA_MS = 3000;

export default function PagoPensionResultadoPage() {
  const [params] = useSearchParams();
  const cuotaId = params.get('cuota');
  const [estado, setEstado] = useState('verificando'); // verificando | pagada | pendiente | error

  useEffect(() => {
    if (!cuotaId) { setEstado('error'); return; }

    let cancelado = false;
    (async () => {
      for (let intento = 0; intento < REINTENTOS; intento++) {
        if (cancelado) return;
        try {
          const misMatriculas = await meService.getMisMatriculasInstitucionales();
          for (const m of misMatriculas) {
            const cuotas = await pensionService.getCuotas(m.institutionId, m.id);
            const cuota = cuotas.find((c) => c.id === cuotaId);
            if (cuota?.status === 'Pagada') {
              if (!cancelado) setEstado('pagada');
              return;
            }
          }
        } catch { /* reintenta */ }
        await new Promise((r) => setTimeout(r, ESPERA_MS));
      }
      if (!cancelado) setEstado('pendiente');
    })();

    return () => { cancelado = true; };
  }, [cuotaId]);

  return (
    <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
      {estado === 'verificando' && (
        <>
          <CircularProgress sx={{ mb: 3, color: '#2e7d32' }} />
          <Typography variant="h6" fontWeight={700}>Confirmando tu pago…</Typography>
          <Typography color="text.secondary">Esto puede tardar unos segundos.</Typography>
        </>
      )}

      {estado === 'pagada' && (
        <>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>¡Pago confirmado!</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>Tu cuota quedó registrada.</Typography>
          <Button component={RouterLink} to="/mi-institucion/pensiones" variant="contained" sx={{ bgcolor: '#2e7d32' }}>
            Ver mis pensiones
          </Button>
        </>
      )}

      {estado === 'pendiente' && (
        <>
          <HourglassEmptyIcon sx={{ fontSize: 64, color: '#f9a825', mb: 2 }} />
          <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Todavía procesando</Typography>
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            Wompi confirmó tu transacción, pero puede tardar un poco más en reflejarse aquí.
            Si no se actualiza en unos minutos, contacta a tu institución con tu comprobante de pago.
          </Alert>
          <Button component={RouterLink} to="/mi-institucion/pensiones" variant="outlined">
            Ver mis pensiones
          </Button>
        </>
      )}

      {estado === 'error' && (
        <Alert severity="error">No se pudo verificar el pago. Contacta a tu institución.</Alert>
      )}
    </Container>
  );
}
